import React, { useState, useEffect, useRef } from 'react';

// DatePicker Component with max date = today
const DatePicker = ({ 
  selectedDate, 
  onChange, 
  onClose,
  minYear = 1900,
  position = 'below'
}) => {
  // Start with months view instead of years
  const [view, setView] = useState('months');
  
  // Get today's date
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();
  
  // Use selectedDate if provided, otherwise use today as default
  const initialDate = selectedDate ? new Date(selectedDate) : today;
  
  const [displayYear, setDisplayYear] = useState(initialDate.getFullYear());
  const [displayMonth, setDisplayMonth] = useState(initialDate.getMonth());
  
  // Get max limits from today
  const maxYear = currentYear;
  const maxMonth = currentMonth;
  const maxDay = currentDay;
  
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr',
    'May', 'Jun', 'Jul', 'Aug',
    'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Update display when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      setDisplayYear(date.getFullYear());
      setDisplayMonth(date.getMonth());
    } else {
      // Reset to today if no date is selected
      setDisplayYear(currentYear);
      setDisplayMonth(currentMonth);
    }
  }, [selectedDate, currentYear, currentMonth]);

  const generateYears = () => {
    const years = [];
    const startYear = Math.floor((displayYear - minYear) / 12) * 12 + minYear;
    
    for (let i = 0; i < 12; i++) {
      const year = startYear + i;
      if (year >= minYear && year <= maxYear) {
        years.push(year);
      }
    }
    
    return years;
  };

  const generateDays = () => {
    const firstDay = new Date(displayYear, displayMonth, 1);
    const lastDay = new Date(displayYear, displayMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayYear, displayMonth, day);
      days.push(date);
    }
    
    return days;
  };

  // Check if a date is disabled (in the future)
  const isDateDisabled = (date) => {
    return date > today;
  };

  // Check if a month is disabled (all future)
  const isMonthDisabled = (monthIndex) => {
    // If year is current year, disable months after current month
    if (displayYear === maxYear) {
      return monthIndex > maxMonth;
    }
    // If year is in the future, all months are disabled
    return displayYear > maxYear;
  };

  // Check if a year is disabled (future years)
  const isYearDisabled = (year) => {
    return year > maxYear;
  };

  const navigateYears = (direction) => {
    setDisplayYear(prev => {
      const newYear = prev + (direction === 'next' ? 12 : -12);
      return Math.min(Math.max(newYear, minYear), maxYear);
    });
  };

  // In months view, navigate year by year instead of month by month
  const navigateMonthsView = (direction) => {
    const newYear = displayYear + (direction === 'next' ? 1 : -1);
    
    // Don't navigate to future years
    if (newYear > maxYear) {
      return;
    }
    
    // Don't navigate below min year
    if (newYear < minYear) {
      return;
    }
    
    setDisplayYear(newYear);
    // Keep the same month displayed when changing years
  };

  // In days view, navigate month by month
  const navigateDaysView = (direction) => {
    let newYear = displayYear;
    let newMonth = displayMonth + (direction === 'next' ? 1 : -1);
    
    // Don't navigate to future months
    if (newYear === maxYear && newMonth > maxMonth) {
      return;
    }
    
    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    
    // Don't navigate to future years
    if (newYear > maxYear) {
      return;
    }
    
    setDisplayYear(newYear);
    setDisplayMonth(newMonth);
  };

  const handleYearSelect = (year) => {
    if (isYearDisabled(year)) return;
    setDisplayYear(year);
    setView('months');
  };

  const handleMonthSelect = (monthIndex) => {
    if (isMonthDisabled(monthIndex)) return;
    setDisplayMonth(monthIndex);
    setView('days');
  };

  const handleDaySelect = (date) => {
    if (isDateDisabled(date)) return;
    if (onChange) {
      onChange(date);
    }
    if (onClose) {
      onClose();
    }
  };

  const isTodayDate = (date) => {
    return date.getDate() === currentDay &&
           date.getMonth() === currentMonth &&
           date.getFullYear() === currentYear;
  };

  const isSelectedDate = (date) => {
    if (!selectedDate) return false;
    const selDate = new Date(selectedDate);
    return date.getDate() === selDate.getDate() &&
           date.getMonth() === selDate.getMonth() &&
           date.getFullYear() === selDate.getFullYear();
  };

  const getSelectedYear = () => {
    if (!selectedDate) return null;
    try {
      const date = new Date(selectedDate);
      if (isNaN(date.getTime())) return null;
      return date.getFullYear();
    } catch (error) {
      return null;
    }
  };

  const getSelectedMonth = () => {
    if (!selectedDate) return null;
    try {
      const date = new Date(selectedDate);
      if (isNaN(date.getTime())) return null;
      return date.getMonth();
    } catch (error) {
      return null;
    }
  };

  const styles = {
    container: {
      position: 'absolute',
      top: position === 'below' ? '100%' : 'auto',
      bottom: position === 'above' ? '100%' : 'auto',
      left: 0,
      zIndex: 1000,
      fontFamily: "Arial, sans-serif",
      width: '240px',
      backgroundColor: 'white',
      borderRadius: '0',
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      border: '1px solid #e0e0e0',
      marginTop: position === 'below' ? '2px' : 0,
      marginBottom: position === 'above' ? '2px' : 0,
    },
    gridContainer: {
      padding: '15px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '8px',
    },
    daysGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: '4px',
    },
    cell: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: 'white',
      border: 'none',
      borderRadius: '0',
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: '#333',
    },
    selectedCell: {
      backgroundColor: '#00558c',
      color: '#FFFFFF',
      fontWeight: '600',
    },
    todayCell: {
      backgroundColor: '#d9d9d9',
      color: '#333',
      fontWeight: '600',
    },
    disabledCell: {
      color: '#cccccc',
      cursor: 'not-allowed',
      backgroundColor: '#f9f9f9',
    },
    emptyCell: {
      height: '40px',
    },
    daysHeader: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      marginBottom: '10px',
    },
    dayHeaderCell: {
      fontSize: '10px',
      color: '#999999',
      textAlign: 'center',
      fontWeight: '500',
      textTransform: 'uppercase',
    },
    navigation: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '15px',
      backgroundColor: '#00558c',
      borderBottom: '1px solid #e0e0e0',
    },
    navButton: {
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '24px',
      color: '#FFF',
      cursor: 'pointer',
      padding: '5px 10px',
      borderRadius: '4px',
    },
    disabledNavButton: {
      color: '#cccccc',
      cursor: 'not-allowed',
    },
    navTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#FFF',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      border: 'none',
      padding: '8px 12px',
      borderRadius: '4px',
      textAlign: 'center',
      minWidth: '100px',
    },
    footer: {
      padding: '12px',
      borderTop: '1px solid #e0e0e0',
      backgroundColor: '#fafafa',
      fontSize: '11px',
      color: '#666666',
      textAlign: 'center',
    },
  };

  const renderYears = () => {
    const years = generateYears();
    const selectedYear = getSelectedYear();
    
    return (
      <div style={styles.gridContainer}>
        <div style={styles.grid}>
          {years.map((year, index) => {
            const disabled = isYearDisabled(year);
            const isSelected = year === selectedYear && !disabled;
            const isTodayYear = year === currentYear && !disabled;
            
            return (
              <button
                key={index}
                type="button"
                style={{
                  ...styles.cell,
                  ...(disabled ? styles.disabledCell : {}),
                  ...(isSelected ? styles.selectedCell : {}),
                  ...(isTodayYear ? styles.todayCell : {}),
                  color: disabled ? '#ccc' : isSelected ? '#FFFFFF' : '#333',
                }}
                onClick={() => !disabled && handleYearSelect(year)}
                onMouseOver={(e) => {
                  if (!disabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                    e.currentTarget.style.color = '#333';
                  }
                }}
                onMouseOut={(e) => {
                  if (!disabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#333';
                  }
                }}
                disabled={disabled}
              >
                {year}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonths = () => {
    const selectedYear = getSelectedYear();
    const selectedMonth = getSelectedMonth();
    
    return (
      <div style={styles.gridContainer}>
        <div style={styles.grid}>
          {months.map((month, index) => {
            const disabled = isMonthDisabled(index);
            
            // Check if we have a selected date
            const hasSelectedDate = selectedDate !== null && selectedDate !== undefined;
            
            // Only highlight if:
            // 1. We have a selected date
            // 2. We're viewing the same year as the selected date
            // 3. This month is the selected month
            // 4. The month is not disabled
            const isSelected = hasSelectedDate && 
                              selectedYear !== null &&
                              selectedMonth !== null &&
                              displayYear === selectedYear && 
                              index === selectedMonth && 
                              !disabled;
            
            const isTodayMonth = index === currentMonth && displayYear === currentYear && !disabled;
            
            return (
              <button
                key={index}
                type="button"
                style={{
                  ...styles.cell,
                  ...(disabled ? styles.disabledCell : {}),
                  ...(isSelected ? styles.selectedCell : {}),
                  ...(isTodayMonth ? styles.todayCell : {}),
                  color: disabled ? '#ccc' : isSelected ? '#FFFFFF' : '#333',
                }}
                onClick={() => !disabled && handleMonthSelect(index)}
                onMouseOver={(e) => {
                  if (!disabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                    e.currentTarget.style.color = '#333';
                  }
                }}
                onMouseOut={(e) => {
                  if (!disabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#333';
                  }
                }}
                disabled={disabled}
              >
                {month}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const days = generateDays();
    const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    
    return (
      <div style={styles.gridContainer}>
        <div style={styles.daysHeader}>
          {dayHeaders.map((day, index) => (
            <div key={index} style={styles.dayHeaderCell}>
              {day}
            </div>
          ))}
        </div>
        <div style={styles.daysGrid}>
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} style={styles.emptyCell}></div>;
            }
            
            const disabled = isDateDisabled(date);
            const isToday = isTodayDate(date);
            const isSelected = isSelectedDate(date);
            
            return (
              <button
                key={index}
                type="button"
                style={{
                  ...styles.cell,
                  ...(disabled ? styles.disabledCell : {}),
                  ...(isToday && !disabled ? styles.todayCell : {}),
                  ...(isSelected && !disabled ? styles.selectedCell : {}),
                  color: disabled ? '#ccc' : isSelected ? '#FFFFFF' : '#333',
                }}
                onClick={() => !disabled && handleDaySelect(date)}
                onMouseOver={(e) => {
                  if (!disabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                    e.currentTarget.style.color = '#333';
                  }
                }}
                onMouseOut={(e) => {
                  if (!disabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#333';
                  }
                }}
                disabled={disabled}
                title={disabled ? "Cannot select future dates" : ""}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Get title based on current display state
  const getHeaderTitle = () => {
    switch (view) {
      case 'years':
        return `To ${displayYear}`;
      case 'months':
        return displayYear.toString();
      case 'days':
        return `${months[displayMonth]} ${displayYear}`;
      default:
        return '';
    }
  };

  // Check if next navigation is allowed
  const canNavigateNext = () => {
    if (view === 'years') {
      const years = generateYears();
      const lastYear = years[years.length - 1];
      return lastYear < maxYear;
    } else if (view === 'months') {
      // In months view, check if we can go to next year
      return displayYear < maxYear;
    } else if (view === 'days') {
      if (displayYear > maxYear) return false;
      if (displayYear === maxYear) {
        return displayMonth < maxMonth;
      }
      return true;
    }
    return true;
  };

  // Check if previous navigation is allowed
  const canNavigatePrev = () => {
    if (view === 'years') {
      const years = generateYears();
      const firstYear = years[0];
      return firstYear > minYear;
    } else if (view === 'months') {
      // In months view, check if we can go to previous year
      return displayYear > minYear;
    } else if (view === 'days') {
      // In days view, always allow going back in months
      return true;
    }
    return true;
  };

  const handlePrevClick = (e) => {
    e.preventDefault();
    if (view === 'years' && !canNavigatePrev()) return;
    if (view === 'years') navigateYears('prev');
    else if (view === 'months') navigateMonthsView('prev');
    else if (view === 'days') navigateDaysView('prev');
  };

  const handleNextClick = (e) => {
    e.preventDefault();
    if (view === 'years' && !canNavigateNext()) return;
    if (view === 'years') navigateYears('next');
    else if (view === 'months') navigateMonthsView('next');
    else if (view === 'days') navigateDaysView('next');
  };

  const handleNavTitleClick = (e) => {
    e.preventDefault();
    if (view === 'days') setView('months');
    else if (view === 'months') setView('years');
  };

  return (
    <div style={styles.container}>
      <div style={styles.navigation}>
        <button
          type="button"
          style={{
            ...styles.navButton,
            ...(!canNavigatePrev() ? styles.disabledNavButton : {})
          }}
          onClick={handlePrevClick}
          onMouseOver={(e) => {
            if (canNavigatePrev()) {
              e.currentTarget.style.backgroundColor = '#004070';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          disabled={!canNavigatePrev()}
        >
          ‹
        </button>
        
        <button
          type="button"
          style={styles.navTitle}
          onClick={handleNavTitleClick}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#004070'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          {getHeaderTitle()}
        </button>
        
        <button
          type="button"
          style={{
            ...styles.navButton,
            ...(!canNavigateNext() ? styles.disabledNavButton : {})
          }}
          onClick={handleNextClick}
          onMouseOver={(e) => {
            if (canNavigateNext()) {
              e.currentTarget.style.backgroundColor = '#004070';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          disabled={!canNavigateNext()}
        >
          ›
        </button>
      </div>
      
      {/* Main content - Shows months by default */}
      {view === 'years' && renderYears()}
      {view === 'months' && renderMonths()}
      {view === 'days' && renderDays()}
      
      {/* Footer */}
      <div style={styles.footer}>
        {view === 'days' ? 'Select a date (future dates disabled)' : 
         view === 'months' ? 'Select a month (click arrows to change year)' : 
         'Select a year'}
      </div>
    </div>
  );
};

// DateInput Component with default current date
const DateInput = ({ 
  value,
  onChange,
  placeholder = "Select date...",
  minYear = 1900,
  format = "MM/dd/yyyy",
  showIcon = true,
  defaultValue = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpening, setIsOpening] = useState(false); // Track if we're in the process of opening
  
  // Use defaultValue if provided, otherwise use today's date
  const defaultDate = defaultValue || new Date();
  const [dateValue, setDateValue] = useState(value || defaultDate);
  
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Update when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setDateValue(value);
    }
  }, [value]);

  // Initialize with default date on first render if no value provided
  useEffect(() => {
    if (value === undefined && defaultValue === null) {
      const today = new Date();
      setDateValue(today);
      if (onChange) {
        onChange(today);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if we're in the process of opening
      if (isOpening) {
        setIsOpening(false);
        return;
      }
      
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Use a timeout to ensure click events are processed in the right order
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpening]);

  const formatDate = (date) => {
    if (!date) return '';
    
    const d = new Date(date);
    switch (format) {
      case 'dd/MM/yyyy':
        return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
      case 'yyyy-MM-dd':
        return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
      case 'MM/dd/yyyy':
      default:
        return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
    }
  };

  const handleDateChange = (date) => {
    setDateValue(date);
    if (onChange) {
      onChange(date);
    }
  };

  const handleInputClick = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling up
    
    // Mark that we're opening the picker
    setIsOpening(true);
    
    // Always open the picker, don't toggle
    setIsOpen(true);
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Reset the opening flag after a short delay
    setTimeout(() => {
      setIsOpening(false);
    }, 100);
  };

  const handleIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling up
    
    // Mark that we're opening the picker
    setIsOpening(true);
    
    // Always open the picker, don't toggle
    setIsOpen(true);
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Reset the opening flag after a short delay
    setTimeout(() => {
      setIsOpening(false);
    }, 100);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpening(true);
      setIsOpen(true);
      
      setTimeout(() => {
        setIsOpening(false);
      }, 100);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handlePickerClose = () => {
    setIsOpen(false);
  };

  const inputStyles = {
    container: {
      position: 'relative',
      display: 'inline-block',
      width: '100%',
      fontFamily: "var(--font-500)",
      fontSize: "16px",
      color: "var(--text-color)",
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      paddingRight: showIcon ? '35px' : '12px',
      fontSize: '16px',
      borderRadius: '4px',
      backgroundColor: 'white',
      cursor: 'pointer',
      boxSizing: 'border-box',
      fontFamily: "var(--font-500)",
      border: "2px solid #E3E3E3",
    },
    inputFocus: {
      outline: 'none',
      borderColor: '#00558c',
    },
    icon: {
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#666',
      cursor: 'pointer',
      fontSize: '16px',
      userSelect: 'none',
    },
  };

  const getPickerPosition = () => {
    if (!containerRef.current) return 'below';
    
    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    return spaceBelow < 300 && spaceAbove > 300 ? 'above' : 'below';
  };

  return (
    <div style={inputStyles.container} ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={dateValue ? formatDate(dateValue) : ''}
        onClick={handleInputClick}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        readOnly
        style={{
          ...inputStyles.input,
          ...(isOpen ? inputStyles.inputFocus : {})
        }}
        onFocus={() => {
          // Only open on focus if not already open
          if (!isOpen) {
            setIsOpening(true);
            setIsOpen(true);
            setTimeout(() => {
              setIsOpening(false);
            }, 100);
          }
        }}
      />
      
      {showIcon && (
        <div style={inputStyles.icon} onClick={handleIconClick}>
          📅
        </div>
      )}
      
      {isOpen && (
        <DatePicker
          selectedDate={dateValue}
          onChange={handleDateChange}
          onClose={handlePickerClose}
          minYear={minYear}
          position={getPickerPosition()}
        />
      )}
    </div>
  );
};

// Export both components
export { DatePicker, DateInput };
export default DateInput;